// Skybox tags on note requests (buyer form on /orders). Mirrors the server's
// parseTagList (backend/services/noteSync/tags.js); the server is the authority.

export const TAG_MAX_LEN = 40;
// Mirror backend/services/noteSync/config.js LIMITS (the server enforces them)
export const TAGS_MAX_PER_REQUEST = 5;
export const TAG_OPTIONS_MAX = 20;
// Lion Auto marks the listings it prices with this tag (lionAuto/firstTimeTag.js)
const RESERVED_TAG = 'lion';

/**
 * Why the typed custom tag can't be sent, or null (an empty box is fine).
 * @param {string|null|undefined} text
 * @returns {string|null}
 */
export function customTagProblem(text) {
  const tag = String(text ?? '').trim();
  if (!tag) return null;
  if (/\s/.test(tag)) return 'No spaces in a tag — use - or _ instead';
  if (tag.includes(',')) return 'No commas in a tag';
  if (tag.length > TAG_MAX_LEN) return `A tag must be ${TAG_MAX_LEN} characters or fewer`;
  if (tag.toLowerCase() === RESERVED_TAG) return `"${RESERVED_TAG}" is reserved for Lion Auto`;
  return null;
}

/**
 * Too many tags on one request, or null.
 * @param {string[]|null|undefined} tags  the merged list (mergeTags)
 * @returns {string|null}
 */
export function tagCountProblem(tags) {
  return (tags || []).length > TAGS_MAX_PER_REQUEST ? `At most ${TAGS_MAX_PER_REQUEST} tags per request` : null;
}

/**
 * What's wrong with the admin's tag choices (config tag_options), or null.
 * Split on whitespace and commas like the server; empty is fine (no chips).
 * @param {string|null|undefined} text
 * @returns {string|null}
 */
export function tagOptionsProblem(text) {
  const tags = String(text ?? '').split(/[\s,]+/).filter(Boolean);
  for (const tag of tags) {
    const why = customTagProblem(tag);
    if (why) return `${tag}: ${why}`;
  }
  return mergeTags(tags, '').length > TAG_OPTIONS_MAX ? `At most ${TAG_OPTIONS_MAX} tag choices` : null;
}

/**
 * What a request adds, for status lines: "note", "tags 1test, 2test" or "note + tags 1test".
 * @param {{ categories?: string[], tags?: string[] }|null|undefined} req
 * @returns {string}
 */
export function requestWhat(req) {
  const tags = req?.tags || [];
  if (!tags.length) return 'note';
  const tagText = `tags ${tags.join(', ')}`;
  return (req?.categories || []).length ? `note + ${tagText}` : tagText;
}

/**
 * The final tag list: picked chips, then the typed tag, deduped case-insensitively
 * (first spelling wins). Blank entries are dropped.
 * @param {string[]|null|undefined} picked
 * @param {string|null|undefined} custom
 * @returns {string[]}
 */
export function mergeTags(picked, custom) {
  const out = [];
  const seen = new Set();
  for (const raw of [...(picked || []), custom]) {
    const tag = String(raw ?? '').trim();
    const key = tag.toLowerCase();
    if (!tag || seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
  }
  return out;
}
